// v1
// Path: frontend/src/components/QuickBooksSync/ApplyStandardMappingsModal.jsx
// Inputs: token, group, onClose, onApplied
// Outputs: React JSX modal for previewing and applying standard field mappings en masse to all pipelines in a connection group
// Summary: Batch application interface enabling one-click deployment of standard field mapping presets across all integration pipelines in a connection group.

import React, { useState, useEffect } from 'react';

export const ApplyStandardMappingsModal = ({ token, group, onClose, onApplied }) => {
    const [loading, setLoading] = useState(true);
    const [applying, setApplying] = useState(false);
    const [standardMappings, setStandardMappings] = useState([]);
    const [overwrite, setOverwrite] = useState(false);
    const [selectedConfigIds, setSelectedConfigIds] = useState([]);
    const [sourceSystem, setSourceSystem] = useState('Passport');
    const [availableSystems, setAvailableSystems] = useState(['Passport']);
    const [resultMessage, setResultMessage] = useState(null);

    const pipelines = group.pipelines || [];

    useEffect(() => {
        // Initialize selected configs with all pipeline IDs
        setSelectedConfigIds(pipelines.map(p => p.ConfigID));

        // Fetch standard mappings
        const fetchStandardMappings = async () => {
            setLoading(true);
            try {
                const res = await fetch(`/api/quickbooks/standard-mappings?sourceSystem=${encodeURIComponent(sourceSystem)}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setStandardMappings(Array.isArray(data.mappings) ? data.mappings : []);
                    if (Array.isArray(data.sourceSystems) && data.sourceSystems.length > 0) {
                        setAvailableSystems(data.sourceSystems);
                    }
                }
            } catch (err) {
                console.error('[ApplyStandardMappingsModal] Error fetching standard mappings:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchStandardMappings();
    }, [token, sourceSystem, group]);

    const handleToggleSelectAll = (checked) => {
        if (checked) {
            setSelectedConfigIds(pipelines.map(p => p.ConfigID));
        } else {
            setSelectedConfigIds([]);
        }
    };

    const handleToggleConfig = (configId) => {
        setSelectedConfigIds(prev =>
            prev.includes(configId) ? prev.filter(id => id !== configId) : [...prev, configId]
        );
    };

    const handleApply = async () => {
        if (selectedConfigIds.length === 0) {
            alert('Please select at least one pipeline to apply standard mappings.');
            return;
        }

        setApplying(true);
        setResultMessage(null);

        try {
            const res = await fetch('/api/quickbooks/connection-group/apply-standard-mappings', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    sourceConnPrefix: group.sourceConnPrefix,
                    sourceDatabaseName: group.sourceDatabase,
                    accountId: group.accountId,
                    sourceSystem,
                    overwrite,
                    selectedConfigIds
                })
            });

            const data = await res.json();
            if (res.ok && data.success) {
                setResultMessage({
                    type: 'success',
                    text: data.message || `Successfully applied standard mappings to ${data.updatedCount} pipeline(s).`,
                    details: data.details
                });
                if (onApplied) onApplied();
            } else {
                setResultMessage({
                    type: 'error',
                    text: data.error || 'Failed to apply standard mappings.'
                });
            }
        } catch (err) {
            setResultMessage({
                type: 'error',
                text: err.message
            });
        } finally {
            setApplying(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-3xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-850">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                            <span className="material-symbols-outlined text-[24px]">batch_prediction</span>
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-lg font-bold text-white tracking-wide">
                                    Apply Standard Mappings En Masse
                                </h3>
                                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/30">
                                    Connection Group
                                </span>
                            </div>
                            <p className="text-xs text-slate-400">
                                Deploy canonical field mappings across all pipelines in this connection group simultaneously.
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Connection Group Overview Banner */}
                <div className="px-6 py-3.5 bg-slate-800/80 border-b border-slate-800 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                    <div>
                        <span className="text-slate-400 block text-[11px] font-semibold">Source Database Profile</span>
                        <span className="font-bold text-white font-mono truncate block">
                            {group.profileDisplayName || group.sourceConnPrefix || 'Local Instance'}
                        </span>
                        <span className="text-blue-400 font-mono text-[11px] font-bold">
                            [{group.sourceDatabase}]
                        </span>
                    </div>
                    <div>
                        <span className="text-slate-400 block text-[11px] font-semibold">Target QuickBooks Account</span>
                        <span className="font-bold text-emerald-300 truncate block">
                            {group.accountName || `Account #${group.accountId}`}
                        </span>
                        <span className="text-[11px] text-slate-400 capitalize">
                            {group.accountEnvironment} environment
                        </span>
                    </div>
                    <div>
                        <span className="text-slate-400 block text-[11px] font-semibold">Source System Presets</span>
                        <select
                            value={sourceSystem}
                            onChange={e => setSourceSystem(e.target.value)}
                            className="mt-1 bg-slate-900 border border-slate-700 rounded px-2.5 py-1 text-xs text-white font-semibold focus:border-indigo-500 focus:outline-none"
                        >
                            {availableSystems.map(sys => (
                                <option key={sys} value={sys}>{sys}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Modal Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-5">
                    {resultMessage && (
                        <div className={`p-4 rounded-xl text-xs border space-y-2 ${resultMessage.type === 'success' ? 'bg-emerald-950/70 border-emerald-800 text-emerald-200' : 'bg-red-950/70 border-red-800 text-red-200'}`}>
                            <div className="flex items-center gap-2 font-bold text-sm">
                                <span className="material-symbols-outlined text-[18px]">
                                    {resultMessage.type === 'success' ? 'check_circle' : 'error'}
                                </span>
                                <span>{resultMessage.text}</span>
                            </div>
                            {resultMessage.details && (
                                <ul className="pl-6 list-disc space-y-1 text-[11px] text-emerald-300/80">
                                    {resultMessage.details.map((d, i) => (
                                        <li key={i}>
                                            <strong>{d.configName}</strong> ({d.entityType}): {d.status === 'updated' ? `Updated with ${d.fieldsCount} mapped fields` : `Skipped (${d.reason})`}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    {/* Mode Selection */}
                    <div className="bg-slate-850 border border-slate-700/80 rounded-xl p-4 space-y-3">
                        <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                            <span className="material-symbols-outlined text-[16px] text-indigo-400">tune</span>
                            Application Mode
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <label className={`p-3 rounded-lg border cursor-pointer transition-colors flex items-start gap-2.5 ${!overwrite ? 'bg-indigo-950/40 border-indigo-500/60 text-white' : 'bg-slate-900 border-slate-700/60 text-slate-400 hover:border-slate-600'}`}>
                                <input
                                    type="radio"
                                    name="applyMode"
                                    checked={!overwrite}
                                    onChange={() => setOverwrite(false)}
                                    className="mt-0.5 text-indigo-600 focus:ring-0 cursor-pointer"
                                />
                                <div className="text-xs">
                                    <span className="font-bold block text-indigo-300">Merge / Fill Missing Fields (Safe)</span>
                                    <span className="text-[11px] text-slate-400 block mt-0.5">
                                        Preserves any existing custom field mappings and only appends standard mappings for fields not yet mapped.
                                    </span>
                                </div>
                            </label>

                            <label className={`p-3 rounded-lg border cursor-pointer transition-colors flex items-start gap-2.5 ${overwrite ? 'bg-amber-950/40 border-amber-500/60 text-white' : 'bg-slate-900 border-slate-700/60 text-slate-400 hover:border-slate-600'}`}>
                                <input
                                    type="radio"
                                    name="applyMode"
                                    checked={overwrite}
                                    onChange={() => setOverwrite(true)}
                                    className="mt-0.5 text-amber-600 focus:ring-0 cursor-pointer"
                                />
                                <div className="text-xs">
                                    <span className="font-bold block text-amber-300">Full Overwrite / Replace</span>
                                    <span className="text-[11px] text-slate-400 block mt-0.5">
                                        Completely replaces all existing field mappings in selected pipelines with the canonical standard preset.
                                    </span>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Pipelines Review Table */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                                Group Pipelines to Update ({selectedConfigIds.length} of {pipelines.length} selected)
                            </h4>
                            <div className="flex items-center gap-2 text-xs">
                                <button
                                    type="button"
                                    onClick={() => handleToggleSelectAll(true)}
                                    className="text-indigo-400 hover:text-indigo-300 underline"
                                >
                                    Select All
                                </button>
                                <span className="text-slate-600">|</span>
                                <button
                                    type="button"
                                    onClick={() => handleToggleSelectAll(false)}
                                    className="text-slate-400 hover:text-white underline"
                                >
                                    Deselect All
                                </button>
                            </div>
                        </div>

                        {loading ? (
                            <div className="text-center py-8 text-slate-400 text-xs">
                                <span className="material-symbols-outlined animate-spin text-2xl mb-1 text-indigo-400">sync</span>
                                <p>Checking standard presets...</p>
                            </div>
                        ) : pipelines.length === 0 ? (
                            <div className="p-6 text-center text-slate-400 text-xs bg-slate-800/40 rounded-xl border border-slate-700">
                                No pipelines configured in this connection group.
                            </div>
                        ) : (
                            <div className="border border-slate-700/80 rounded-xl overflow-hidden bg-slate-800/60">
                                <table className="w-full text-left text-xs border-collapse">
                                    <thead>
                                        <tr className="bg-slate-850 text-slate-400 border-b border-slate-700">
                                            <th className="p-3 w-10 text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedConfigIds.length === pipelines.length && pipelines.length > 0}
                                                    onChange={e => handleToggleSelectAll(e.target.checked)}
                                                    className="rounded bg-slate-900 border-slate-700 text-indigo-600 focus:ring-0 cursor-pointer"
                                                />
                                            </th>
                                            <th className="p-3 font-semibold">Pipeline Name</th>
                                            <th className="p-3 font-semibold">Entity Type</th>
                                            <th className="p-3 font-semibold">Current Mappings</th>
                                            <th className="p-3 font-semibold">Standard Preset Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-700/60">
                                        {pipelines.map(p => {
                                            const isSelected = selectedConfigIds.includes(p.ConfigID);
                                            const std = standardMappings.find(m => m.EntityType.toLowerCase() === p.EntityType.toLowerCase());
                                            let currentCount = 0;
                                            try {
                                                const parsed = JSON.parse(p.FieldMappingsJson);
                                                currentCount = Array.isArray(parsed) ? parsed.length : 0;
                                            } catch (e) { currentCount = 0; }

                                            return (
                                                <tr
                                                    key={p.ConfigID}
                                                    onClick={() => handleToggleConfig(p.ConfigID)}
                                                    className={`hover:bg-slate-750 transition-colors cursor-pointer ${isSelected ? 'bg-indigo-950/20' : ''}`}
                                                >
                                                    <td className="p-3 text-center" onClick={e => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => handleToggleConfig(p.ConfigID)}
                                                            className="rounded bg-slate-900 border-slate-700 text-indigo-600 focus:ring-0 cursor-pointer"
                                                        />
                                                    </td>
                                                    <td className="p-3 font-bold text-white">
                                                        {p.ConfigName}
                                                    </td>
                                                    <td className="p-3 font-semibold text-blue-300">
                                                        {p.EntityType}
                                                    </td>
                                                    <td className="p-3 text-slate-300">
                                                        <span className="font-mono">{currentCount}</span> field(s) mapped
                                                    </td>
                                                    <td className="p-3">
                                                        {std ? (
                                                            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-950 text-emerald-300 border border-emerald-800 flex items-center gap-1 w-max">
                                                                <span className="material-symbols-outlined text-[13px]">check</span>
                                                                Available ({std.MappingName || `${std.EntityType} Preset`})
                                                            </span>
                                                        ) : (
                                                            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-slate-800 text-slate-400 border border-slate-700 flex items-center gap-1 w-max">
                                                                <span className="material-symbols-outlined text-[13px]">block</span>
                                                                No standard preset for {p.EntityType}
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {/* Modal Footer */}
                <div className="px-6 py-4 border-t border-slate-800 bg-slate-850 flex items-center justify-between">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-semibold transition-colors border border-slate-700"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleApply}
                        disabled={applying || selectedConfigIds.length === 0}
                        className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-2 shadow-lg shadow-emerald-900/30"
                    >
                        {applying ? (
                            <>
                                <span className="material-symbols-outlined text-[16px] animate-spin">sync</span>
                                Applying Standards...
                            </>
                        ) : (
                            <>
                                <span className="material-symbols-outlined text-[16px]">bolt</span>
                                Apply Standard Mappings to {selectedConfigIds.length} Pipeline{selectedConfigIds.length !== 1 ? 's' : ''}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
